import axios from "axios";

const API = axios.create({
  baseURL: process.env.REACT_APP_API_URL,
});

// Injected after store creation to avoid circular imports
let _store;
export const injectStore = (s) => { _store = s; };

API.interceptors.request.use((config) => {
  const token = localStorage.getItem("token");

  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }

  return config;
});

API.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Token expired or invalid — clear session and let ProtectedRoute redirect
      _store?.dispatch({ type: "auth/logout" });
    }
    return Promise.reject(error);
  }
);

export const loginApi = (email, password) =>
  API.post("/auth/ad-login", { email, password });

export const validateApi = () =>
  API.get("/auth/validation");

export const getProductsApi    = (params = {}) => API.get("/products", { params });
export const getProductByIdApi = (id) => API.get(`/products/${id}`);
export const createProductApi  = (data)     => API.post("/products", data);
export const updateProductApi  = (id, data) => API.put(`/products/${id}`, data);
export const deleteProductApi  = (id)       => API.delete(`/products/${id}`);
export const updateProductStatusApi = (id, active) => API.patch(`/products/${id}/status`, { active });

export const getCategoriesApi   = ()         => API.get("/categories");
export const createCategoryApi  = (data)     => API.post("/categories", data);
export const updateCategoryApi  = (id, data) => API.put(`/categories/${id}`, data);
export const deleteCategoryApi  = (id)       => API.delete(`/categories/${id}`);

export const getActivityTypesApi   = ()         => API.get("/activity-types");
export const createActivityTypeApi = (data)     => API.post("/activity-types", data);
export const updateActivityTypeApi = (id, data) => API.put(`/activity-types/${id}`, data);
export const deleteActivityTypeApi = (id)       => API.delete(`/activity-types/${id}`);

export const getHsnCodesApi   = ()         => API.get("/hsn-codes");
export const createHsnCodeApi = (data)     => API.post("/hsn-codes", data);
export const updateHsnCodeApi = (id, data) => API.put(`/hsn-codes/${id}`, data);
export const deleteHsnCodeApi = (id)       => API.delete(`/hsn-codes/${id}`);

export const getCouponsApi   = ()         => API.get("/api/coupons");
export const createCouponApi = (data)     => API.post("/api/coupons", data);
export const updateCouponApi = (id, data) => API.put(`/api/coupons/${id}`, data);
export const deleteCouponApi = (id)       => API.delete(`/api/coupons/${id}`);


export const getStockByProductApi = (productId) =>
  API.get(`/inventory/stock/product/${productId}`);

export const getStockByVariantApi = (variantId) =>
  API.get(`/inventory/stock/variant/${variantId}`);

// ================= INVENTORY =================
export const getInventoryApi = () => API.get("/inventory");

export const addInventoryApi = (data) =>
  API.post("/inventory", data);

export const deleteInventoryApi = (id) =>
  API.delete(`/inventory/${id}`);

export const updateInventoryApi = (id, data) =>
  API.put(`/inventory/${id}`, data);


// ================= VENDORS =================
export const getVendorsApi = () => API.get("/vendor");

export const createVendorApi = (data) => API.post("/vendor", data);

export const updateVendorApi = (id, data) =>
  API.put(`/vendor/${id}`, data);

export const deleteVendorApi = (id) =>
  API.delete(`/vendor/${id}`);

export const getVendorUsageApi = (id) => API.get(`/vendor/${id}/usage`);

// status: "PENDING" | "APPROVED" | "REJECTED" (omit for all)
export const getVendorsByStatusApi = (status) =>
  API.get("/vendor", { params: status ? { status } : {} });

export const approveVendorApi = (id) => API.post(`/vendor/${id}/approve`);
export const rejectVendorApi = (id, reason) => API.post(`/vendor/${id}/reject`, { reason });

export const approveVendorBankDetailsApi = (id) => API.post(`/vendor/${id}/bank-details/approve`);
export const rejectVendorBankDetailsApi = (id, reason) => API.post(`/vendor/${id}/bank-details/reject`, { reason });

// ================= SETTLEMENTS =================
export const generateSettlementsApi = () => API.post("/settlements/generate");
export const generateVendorSettlementApi = (vendorId) => API.post(`/settlements/generate/${vendorId}`);
export const getSettlementsApi = (params = {}) => API.get("/settlements", { params });
export const getSettlementApi = (id) => API.get(`/settlements/${id}`);
export const getSettlementItemsApi = (id) => API.get(`/settlements/${id}/items`);
export const markSettlementPaidApi = (id, paymentReference) =>
  API.post(`/settlements/${id}/mark-paid`, { paymentReference });

// ================= SALES PERSONS =================
export const getSalesPersonsApi = () => API.get("/sales-person");
export const createSalesPersonApi = (data) => API.post("/sales-person", data);
export const updateSalesPersonApi = (id, data) => API.put(`/sales-person/${id}`, data);
export const deleteSalesPersonApi = (id) => API.delete(`/sales-person/${id}`);


// ================= ORDERS =================
export const getOrdersApi = () => API.get("/orders");

export const createOrderApi = (data) =>
  API.post("/orders", data);

export const deleteOrderApi = (id) =>
  API.delete(`/orders/${id}`);

export const updateOrderStatusApi = (id, status) =>
  API.put(`/orders/${id}/status`, { status });

export const getCodPendingReconciliationApi = () => API.get("/orders/cod-pending-reconciliation");
export const markCodCollectedApi = (orderIds) => API.post("/orders/mark-cod-collected", orderIds);

// ================= SHIPPING (standalone shipping-service microservice) =================
// Real courier-integrated shipments (Shiprocket etc.) — replaces the old manual
// free-text courier/tracking-number entry UI. All proxied through kidsz's
// ShippingController, which authenticates to shipping-service internally; the admin
// panel only ever needs its normal kidsz JWT.
export const getAllShipmentsShippingApi = () => API.get("/api/shipping/shipments");
export const getShipmentsForOrderShippingApi = (orderId) => API.get(`/api/shipping/orders/${orderId}/shipments`);
export const getShipmentShippingApi = (id) => API.get(`/api/shipping/shipments/${id}`);
export const getTrackingShippingApi = (id) => API.get(`/api/shipping/shipments/${id}/tracking`);
export const getLabelShippingApi = (id) => API.get(`/api/shipping/shipments/${id}/label`);
export const schedulePickupShippingApi = (data) => API.post("/api/shipping/pickup-requests", data);
export const getCourierPerformanceShippingApi = (from, to) =>
  API.get("/api/shipping/analytics/courier-performance", { params: { from, to } });
export const getDeliverySlaShippingApi = (from, to, vendorId) =>
  API.get("/api/shipping/analytics/delivery-sla", { params: { from, to, vendorId } });
export const getCostSummaryShippingApi = (from, to, vendorId) =>
  API.get("/api/shipping/analytics/cost-summary", { params: { from, to, vendorId } });


export const getUsersApi = () => API.get("/user");
export const updateUserStatusApi = (id, data) =>
  API.put(`/user/${id}/status`, data);

export const getAnalyticsApi = () => API.get("/analytics/overview");

export const getDashboardApi  = () => API.get("/dashboard/overview");
export const getUserMetricsApi = () => API.get("/dashboard/user-metrics");
export const getProfileSummaryApi = () => API.get("/dashboard/profile-summary");
export const markActivityReadApi = (orderId) => API.post(`/dashboard/activity/${orderId}/read`);

// ================= GUIDANCE =================
export const getGuidanceApi        = (params) => API.get("/api/guidance", { params });
export const createGuidanceApi     = (data)   => API.post("/api/guidance", data);
export const updateGuidanceApi     = (id, data) => API.put(`/api/guidance/${id}`, data);
export const deleteGuidanceApi     = (id)     => API.delete(`/api/guidance/${id}`);

// ================= PARENTING VIDEOS (standalone carousel) =================
export const getParentingVideosApi   = ()         => API.get("/api/parenting-videos/admin/all");
export const createParentingVideoApi = (data)     => API.post("/api/parenting-videos", data);
export const updateParentingVideoApi = (id, data) => API.put(`/api/parenting-videos/${id}`, data);
export const deleteParentingVideoApi = (id)       => API.delete(`/api/parenting-videos/${id}`);

// ================= MONTHLY CONTEST =================
export const getContestsApi          = ()            => API.get("/api/contest");
export const createContestApi        = (data)        => API.post("/api/contest", data);
export const getContestLeaderboardApi = (contestId)  => API.get(`/api/contest/admin/${contestId}/entries`);
export const endContestApi           = (contestId)   => API.post(`/api/contest/${contestId}/end`);
export const createContestEntryApi   = (data)        => API.post("/api/contest/admin/entries", data);
export const updateContestEntryApi   = (id, data)    => API.put(`/api/contest/admin/entries/${id}`, data);
export const setContestEntryActiveApi = (id, active) => API.patch(`/api/contest/admin/entries/${id}/status`, { active });
export const deleteContestEntryApi   = (id)          => API.delete(`/api/contest/admin/entries/${id}`);
export const updateContestDatesApi   = (contestId, data) => API.put(`/api/contest/${contestId}`, data);
export const setContestActiveApi     = (contestId, active) => API.patch(`/api/contest/${contestId}/status`, { active });
export const deleteContestApi        = (contestId)        => API.delete(`/api/contest/${contestId}`);

// ================= FILE UPLOAD (S3) =================
export const uploadImageApi = (file, folder) => {
  const form = new FormData();
  form.append("file", file);
  form.append("folder", folder);
  return API.post("/api/upload", form, {
    headers: { "Content-Type": "multipart/form-data" },
  });
};

export const deleteImageApi = (url) =>
  API.delete(`/api/upload?url=${encodeURIComponent(url)}`);

// ================= VACCINATION CENTERS =================
export const getVaccinationCentersApi    = ()         => API.get("/api/vaccination-centers/admin/all");
export const createVaccinationCenterApi  = (data)     => API.post("/api/vaccination-centers", data);
export const updateVaccinationCenterApi  = (id, data) => API.put(`/api/vaccination-centers/${id}`, data);
export const deleteVaccinationCenterApi  = (id)       => API.delete(`/api/vaccination-centers/${id}`);

// ================= BANNERS =================
export const getBannersApi   = ()         => API.get("/api/banners/admin/all");
export const createBannerApi = (data)     => API.post("/api/banners", data);
export const updateBannerApi = (id, data) => API.put(`/api/banners/${id}`, data);
export const deleteBannerApi = (id)       => API.delete(`/api/banners/${id}`);

// ================= PUSH NOTIFICATIONS =================
export const broadcastNotificationApi   = (data) => API.post("/api/notifications/broadcast", data);
export const getNotificationHistoryApi  = ()     => API.get("/api/notifications/history");

// ================= PROMOTIONAL EMAIL =================
export const sendPromoEmailApi  = (data) => API.post("/api/promo-email/broadcast", data);
export const getEmailHistoryApi = ()     => API.get("/api/promo-email/history");

// ================= RABBITMQ QUEUE MONITOR =================
export const getRabbitQueuesApi  = () => API.get("/api/admin/rabbitmq/queues");
export const getRabbitOverviewApi = () => API.get("/api/admin/rabbitmq/overview");

// ================= TRANSACTIONAL EMAIL LOG =================
export const getTransactionalEmailHistoryApi = (params) =>
  API.get("/api/admin/transactional-emails/history", { params });

// ================= APP SETTINGS =================
export const getAppSettingsApi    = ()     => API.get("/api/app-settings");
export const updateAppSettingsApi = (data) => API.put("/api/app-settings", data);

// ================= CAMPAIGNS (launch countdown, sales, promos) =================
export const getCampaignsApi    = ()         => API.get("/api/campaigns");
export const createCampaignApi  = (data)     => API.post("/api/campaigns", data);
export const updateCampaignApi  = (id, data) => API.put(`/api/campaigns/${id}`, data);
export const deleteCampaignApi  = (id)       => API.delete(`/api/campaigns/${id}`);

// ================= SUBSCRIPTION PLANS =================
export const getSubscriptionPlansApi   = ()         => API.get("/api/subscription-plans/admin/all");
export const createSubscriptionPlanApi = (data)     => API.post("/api/subscription-plans", data);
export const updateSubscriptionPlanApi = (id, data) => API.put(`/api/subscription-plans/${id}`, data);
export const deleteSubscriptionPlanApi = (id)       => API.delete(`/api/subscription-plans/${id}`);

// ================= VENDOR PLANS =================
export const getVendorPlansApi   = ()         => API.get("/api/vendor-plans/admin/all");
export const createVendorPlanApi = (data)     => API.post("/api/vendor-plans", data);
export const updateVendorPlanApi = (id, data) => API.put(`/api/vendor-plans/${id}`, data);
export const deleteVendorPlanApi = (id)       => API.delete(`/api/vendor-plans/${id}`);

// ================= REWARDS (XP & Reward Points) =================
export const getRewardsSummaryApi = () => API.get("/api/rewards/admin/summary");
export const getRewardsTransactionsApi = (userId) => API.get(`/api/rewards/admin/${userId}/transactions`);
